import { inject, Injectable } from '@angular/core';
import { API_URLS, WebServiceProvider } from '@pinnakl/core/web-services';
import {
  PortalClient,
  PortalUser,
  PortalUserClientUpdatePayload,
  PortalUserUpdatePayload,
} from '@pinnakl/investor-portal/domain';
import { getErrorMessage } from '@pinnakl/shared/util-helpers';
import { PinnaklUIToastMessage } from '@pinnakl/shared/util-providers';

@Injectable({
  providedIn: 'root',
})
export class PortalUserApiService {
  private readonly wsp = inject(WebServiceProvider);
  private readonly apiUrls = inject(API_URLS);
  private readonly toast = inject(PinnaklUIToastMessage);

  async loadPortalUser(): Promise<PortalUser> {
    try {
      return this.getUser();
    } catch (error) {
      this.toast.warning('Unable to fetch portal user information. ' + getErrorMessage('', error));
      throw error;
    }
  }

  async updatePortalUser(user: PortalUserUpdatePayload): Promise<PortalUser> {
    try {
      return this.updateUser(user);
    } catch (error) {
      this.toast.warning('Unable to update portal user information. ' + getErrorMessage('', error));
      throw error;
    }
  }

  async updatePortalUserClient(payload: PortalUserClientUpdatePayload): Promise<void> {
    try {
      return this.wsp.put<void>({ endpoint: this.apiUrls.portalUserClientConfig, payload });
    } catch (error) {
      this.toast.warning('Unable to update portal user client information. ' + getErrorMessage('', error));
      throw error;
    }
  }

  async loadPortalClient(): Promise<PortalClient> {
    try {
      return this.wsp.get<PortalClient>({ endpoint: this.apiUrls.portalUserSelectedClient });
    } catch (error) {
      this.toast.warning('Unable to fetch portal client information. ' + getErrorMessage('', error));
      throw error;
    }
  }

  private getUser(): Promise<PortalUser> {
    return this.wsp.get<PortalUser>({ endpoint: this.apiUrls.portalUser });
  }

  private updateUser(payload: PortalUserUpdatePayload): Promise<PortalUser> {
    return this.wsp.put<PortalUser>({ endpoint: this.apiUrls.portalUser, payload });
  }
}
